import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { Plus, Package, CaretDown } from '@phosphor-icons/react';
import sectionStyles from './SpaceItemsSection.module.css';
import ItemConfigWizard from './ItemConfigWizard';
import ItemOptionsEditor from './ItemOptionsEditor';
import TradeCard from '../../TradeCard';
import SpaceWindowsSection from '../Windows/SpaceWindowsSection';
import { useData } from '../../../../hooks/useData';
import { useApp } from '../../../../contexts/AppContext';
import { useErrorLogging, ERROR_SEVERITY, ERROR_CATEGORY } from '../../../../utils/errorLogging';
import { TABLES, CONSTRUCTION_TYPE_IDS } from '../../../../constants';
import { MODAL_TYPES } from '../../../../constants/modalTypes';
import {
  calculateTotalWithVAT,
  getEffectiveEstimatedCost,
} from '../../../../utils/costCalculations';
import { formatCurrencyShort } from '../../../../utils/formatters';

// Phase ID for כללי items (whole-home items)
const KLALI_PHASE_ID = 'e7efa2ea-723d-44e2-9659-467a61b2fd38';

// Helper: check if a master item is a construction type
const isConstructionType = itemTypeId => CONSTRUCTION_TYPE_IDS.includes(itemTypeId);

const SpaceItemsSection = React.memo(
  function SpaceItemsSection({ spaceId, space, isKlali = false, isConstruction = false }) {
    const { currentProject, openModal } = useApp();
    const [wizardItems, setWizardItems] = useState([]);
    const [showWizard, setShowWizard] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [expandedCardKey, setExpandedCardKey] = useState(null); // Zone A accordion
    const [expandedPillKey, setExpandedPillKey] = useState(null); // Zone B accordion
    // Track which space we've already applied the default-expand for, so that
    // collapsing a card stays collapsed until the user switches spaces.
    const defaultExpandedForSpaceRef = useRef(null);
    const { logError } = useErrorLogging();

    // ─── Data fetching ──────────────────────────────────────────────

    const masterItemsFilter = useMemo(() => {
      if (isKlali) return { project_phase_id: KLALI_PHASE_ID };
      if (isConstruction) return { item_type_id: CONSTRUCTION_TYPE_IDS };
      return undefined;
    }, [isKlali, isConstruction]);

    const { data: allMasterItems } = useData(TABLES.MASTER_ITEMS, {
      queryKey: [TABLES.MASTER_ITEMS, spaceId, currentProject?.id, isKlali, isConstruction],
      select: `id, item_name, estimated_cost, is_bundle, include_in_boq, bundle_type, bundle_items, item_options, cost_type_id, project_phase_id, item_type_id, vendor_type_id, bb_chapter_id, display_order`,
      filter: masterItemsFilter,
      realtime: false,
    });

    const masterItems = useMemo(() => {
      if (!allMasterItems) return allMasterItems;
      if (isKlali) return allMasterItems.filter(mi => !isConstructionType(mi.item_type_id));
      return allMasterItems;
    }, [allMasterItems, isKlali]);

    const { data: costTypes } = useData(TABLES.REFERENCE_COST_TYPES, {
      select: 'id,name',
      realtime: false,
    });

    // Fetch window count for regular spaces
    const { data: projectWindows } = useData(TABLES.PROJECT_WINDOWS, {
      select: 'id',
      filter: { space_id: spaceId },
      enabled: !!spaceId && !isKlali && !isConstruction,
      realtime: true,
    });

    // BB chapters for hybrid grouping (depth 0 + depth 1 for שונות parent)
    const { data: bbChapters } = useData(TABLES.REFERENCE_BB_CHAPTERS, {
      select: 'id, friendly_name, name_he, depth, parent_id, code',
      realtime: false,
    });

    const { data: suggestedItems } = useData(TABLES.REFERENCE_SUGGESTED_ITEMS, {
      select: '*',
      filter: { master_space_id: space?.master_space_id },
      realtime: false,
      enabled: !!space?.master_space_id,
    });

    // Virtual spaces (כללי, בנייה) should hide master items that are assigned
    // to any master_space via reference_suggested_items — those items belong
    // at the per-room level, not in the whole-home view. This also excludes
    // bundle children whose PARENT bundle is space-assigned (e.g. אמבטיית קרח
    // is a child of בריכה / ספא which is assigned to outdoor spaces, so it
    // shouldn't be pickable from בנייה either).
    const { data: spaceAssignedItemRefs } = useData(TABLES.REFERENCE_SUGGESTED_ITEMS, {
      queryKey: [TABLES.REFERENCE_SUGGESTED_ITEMS, 'virtual-exclude'],
      select: 'master_item_id, master_items(id, bundle_items)',
      realtime: false,
      enabled: isKlali || isConstruction,
    });

    const spaceAssignedMasterIds = useMemo(() => {
      if (!(isKlali || isConstruction) || !spaceAssignedItemRefs) return null;
      const ids = new Set();
      spaceAssignedItemRefs.forEach(r => {
        if (r.master_item_id) ids.add(r.master_item_id);
        const children = r.master_items?.bundle_items?.items;
        if (Array.isArray(children)) {
          children.forEach(c => {
            if (c?.item_id) ids.add(c.item_id);
          });
        }
      });
      return ids;
    }, [spaceAssignedItemRefs, isKlali, isConstruction]);

    // masterItems to show in the picker / derive trade cards from.
    // Regular space: same as full masterItems.
    // Virtual space: excludes items assigned to any master_space.
    const catalogMasterItems = useMemo(() => {
      if (!masterItems) return masterItems;
      if (!spaceAssignedMasterIds) return masterItems;
      return masterItems.filter(mi => !spaceAssignedMasterIds.has(mi.id));
    }, [masterItems, spaceAssignedMasterIds]);

    const { data: projectDefaultOptions } = useData(TABLES.PROJECT_DEFAULT_OPTIONS, {
      select: 'master_item_id, selected_option, master_space_id, floor_id',
      filter: { project_id: currentProject?.id },
      realtime: false,
      enabled: !!currentProject?.id,
    });

    const {
      data: projectItems,
      error: projectItemsError,
      isLoading: projectItemsLoading,
      manageProjectItems,
      deleteProjectItem,
    } = useData(TABLES.PROJECT_ITEMS, {
      queryKey: [TABLES.PROJECT_ITEMS, spaceId, currentProject?.id, isKlali, isConstruction],
      enabled: Boolean(currentProject?.id) && (Boolean(spaceId) || isKlali || isConstruction),
      select: `*, master_items(id, item_name, cost_type_id, bb_chapter_id, item_type_id, vendor_type_id), vendor:project_vendors(master_vendor_id, master_vendors(id, name))`,
      filter:
        isKlali || isConstruction
          ? { project_id: currentProject?.id, space_id: null }
          : { space_id: spaceId },
      realtime: true,
      onError: error => {
        logError(error, {
          severity: ERROR_SEVERITY.ERROR,
          category: ERROR_CATEGORY.DATA_FETCH,
          message: 'Error fetching project items',
        });
      },
    });

    // ─── Computed data ──────────────────────────────────────────────

    const filteredProjectItems = useMemo(() => {
      if (!projectItems) return projectItems;
      if (isKlali) {
        return projectItems.filter(pi => !isConstructionType(pi.master_items?.item_type_id));
      }
      if (isConstruction) {
        return projectItems.filter(pi => isConstructionType(pi.master_items?.item_type_id));
      }
      return projectItems;
    }, [projectItems, isKlali, isConstruction]);

    // ─── BB hybrid grouping ────────────────────────────────────────
    // Map bb_chapter_id → group key (depth-0 parent, except שונות which uses depth-1)
    const bbGroupMap = useMemo(() => {
      if (!bbChapters) return {};
      const chapterById = new Map(bbChapters.map(ch => [ch.id, ch]));
      const map = {};

      bbChapters.forEach(ch => {
        let parent = ch;
        if (ch.depth > 0 && ch.parent_id) {
          const p = chapterById.get(ch.parent_id);
          if (p) parent = p;
        }

        const parentName = parent.friendly_name || parent.name_he;
        if (parentName === 'שונות' && ch.depth > 0) {
          // שונות children get their own card, sorted after all depth-0 chapters
          map[ch.id] = {
            key: ch.id,
            name: ch.friendly_name || ch.name_he,
            sortOrder: 100 + (ch.display_order || 0),
          };
        } else {
          map[ch.id] = {
            key: parent.id,
            name: parentName,
            sortOrder: parent.display_order ?? parseInt(parent.code) ?? 999,
          };
        }
      });
      return map;
    }, [bbChapters]);

    // Resolve a master item's group key
    const getGroupKey = useCallback(
      masterItemOrId => {
        const bbChapterId =
          typeof masterItemOrId === 'string'
            ? masterItems?.find(mi => mi.id === masterItemOrId)?.bb_chapter_id
            : masterItemOrId?.bb_chapter_id;
        if (!bbChapterId) return '__other__';
        return bbGroupMap[bbChapterId]?.key || '__other__';
      },
      [bbGroupMap, masterItems],
    );

    const getGroupInfo = useCallback(
      key => {
        if (key === '__other__') return { name: 'אחר', sortOrder: 9999 };
        // Find sortOrder from bbGroupMap (any entry pointing to this key)
        const entry = Object.values(bbGroupMap).find(e => e.key === key);
        if (entry) return { name: entry.name, sortOrder: entry.sortOrder };
        const ch = bbChapters?.find(c => c.id === key);
        if (!ch) return { name: 'אחר', sortOrder: 9999 };
        return { name: ch.friendly_name || ch.name_he, sortOrder: ch.display_order ?? 999 };
      },
      [bbChapters, bbGroupMap],
    );

    // Per-space ordering map from reference_suggested_items.display_order,
    // keyed by master_item_id. Virtual spaces have no entries — callers fall
    // back to master_items.display_order.
    const spaceOrderMap = useMemo(() => {
      const map = new Map();
      (suggestedItems || []).forEach(si => {
        map.set(si.master_item_id, si.display_order ?? 9999);
      });
      return map;
    }, [suggestedItems]);

    // Resolve the effective master_item_id for sorting: simple-bundle children
    // sort under their parent bundle so the bundle stays contiguous. Choice
    // bundle children (and standalones) sort by their own master_item_id.
    const getItemSortKey = useCallback(
      item => {
        const bundleMasterId = item.relationships?.bundle?.master_item_id;
        let effMasterId = item.master_item_id;
        if (bundleMasterId) {
          const parent = masterItems?.find(mi => mi.id === bundleMasterId);
          if (parent && parent.bundle_type !== 'choice') {
            effMasterId = bundleMasterId;
          }
        }
        const master = masterItems?.find(mi => mi.id === effMasterId);
        return {
          primary: spaceOrderMap.get(effMasterId) ?? 9999,
          secondary: master?.display_order ?? 9999,
          createdAt: item.created_at || '',
        };
      },
      [spaceOrderMap, masterItems],
    );

    // Group project items by BB hybrid — exclude window-linked items
    const itemsByGroup = useMemo(() => {
      if (!filteredProjectItems) return {};
      const groups = {};

      filteredProjectItems.forEach(item => {
        if (item.details?.window_id) return;

        let groupKey;
        // Bundle items: group by parent bundle's BB chapter (keep bundle together)
        // Exception: choice bundle children use their own BB chapter
        const bundleMasterItemId = item.relationships?.bundle?.master_item_id;
        if (bundleMasterItemId) {
          const parentBundle = masterItems?.find(mi => mi.id === bundleMasterItemId);
          const isChoiceBundle = parentBundle?.bundle_type === 'choice';
          if (isChoiceBundle) {
            // Choice bundle child — group by the child's own BB chapter
            const bbChapterId = item.master_items?.bb_chapter_id;
            groupKey =
              bbChapterId && bbGroupMap[bbChapterId] ? bbGroupMap[bbChapterId].key : '__other__';
          } else {
            groupKey = getGroupKey(bundleMasterItemId);
          }
        } else {
          const bbChapterId = item.master_items?.bb_chapter_id;
          if (bbChapterId && bbGroupMap[bbChapterId]) {
            groupKey = bbGroupMap[bbChapterId].key;
          } else {
            groupKey = '__other__';
          }
        }

        if (!groups[groupKey]) groups[groupKey] = [];
        groups[groupKey].push(item);
      });

      // Sort each group so item rows respect reference_suggested_items.display_order
      // first, then master_items.display_order, then created_at.
      Object.keys(groups).forEach(key => {
        groups[key].sort((a, b) => {
          const ka = getItemSortKey(a);
          const kb = getItemSortKey(b);
          if (ka.primary !== kb.primary) return ka.primary - kb.primary;
          if (ka.secondary !== kb.secondary) return ka.secondary - kb.secondary;
          return ka.createdAt.localeCompare(kb.createdAt);
        });
      });

      return groups;
    }, [filteredProjectItems, bbGroupMap, getGroupKey, masterItems, getItemSortKey]);

    // Group keys relevant to this space.
    // - Regular space: derived from reference_suggested_items.
    // - Virtual spaces (כללי, בנייה): derived from catalog master items (which
    //   already exclude items assigned to any master_space), so the trade cards
    //   match what's actually addable from the whole-home view.
    const relevantGroupKeys = useMemo(() => {
      const keys = new Set();

      if (isKlali || isConstruction) {
        if (!catalogMasterItems) return new Set();
        catalogMasterItems.forEach(mi => keys.add(getGroupKey(mi)));
      } else {
        if (!masterItems || !suggestedItems) return new Set();
        const suggestedMasterIds = new Set(suggestedItems.map(si => si.master_item_id));
        masterItems.forEach(mi => {
          if (suggestedMasterIds.has(mi.id)) {
            keys.add(getGroupKey(mi));
          }
        });
      }

      keys.delete('__other__');
      return keys;
    }, [suggestedItems, masterItems, catalogMasterItems, getGroupKey, isKlali, isConstruction]);

    // Zone A: cards that have items, sorted by trade display_order
    const cardsWithItems = useMemo(() => {
      return Object.entries(itemsByGroup)
        .map(([key, items]) => {
          const info = getGroupInfo(key);
          return {
            key,
            vendorType: { id: key, name: info.name, display_order: info.sortOrder },
            items,
          };
        })
        .sort((a, b) => (a.vendorType.display_order || 999) - (b.vendorType.display_order || 999));
    }, [itemsByGroup, getGroupInfo]);

    // Default the first Zone A card to expanded so users discover the accordion
    // affordance. Only fires once per space — collapsing stays collapsed until
    // the user navigates to a different space.
    useEffect(() => {
      if (defaultExpandedForSpaceRef.current === spaceId) return;
      if (cardsWithItems.length > 0) {
        setExpandedCardKey(cardsWithItems[0].key);
        defaultExpandedForSpaceRef.current = spaceId;
      }
    }, [spaceId, cardsWithItems]);

    // Zone B: ALL relevant categories, with currentCount for badge display
    const catalogCards = useMemo(() => {
      return Array.from(relevantGroupKeys)
        .map(groupKey => {
          const info = getGroupInfo(groupKey);
          const currentCount = (itemsByGroup[groupKey] || []).filter(
            i => i.relationships?.bundle?.role !== 'parent',
          ).length;
          return {
            key: groupKey,
            vendorType: { id: groupKey, name: info.name, display_order: info.sortOrder },
            currentCount,
          };
        })
        .sort((a, b) => (a.vendorType.display_order || 999) - (b.vendorType.display_order || 999));
    }, [relevantGroupKeys, getGroupInfo, itemsByGroup]);

    // Missing suggested items per group — count of unadded suggested items keyed by trade card group
    const missingSuggestedByGroup = useMemo(() => {
      if (!suggestedItems || !masterItems || !filteredProjectItems) return {};
      const addedMasterIds = new Set();
      (filteredProjectItems || []).forEach(pi => {
        if (pi.master_item_id) addedMasterIds.add(pi.master_item_id);
        if (pi.relationships?.bundle?.master_item_id) {
          addedMasterIds.add(pi.relationships.bundle.master_item_id);
        }
      });

      const byGroup = {};
      suggestedItems
        .filter(si => si.is_suggested && !addedMasterIds.has(si.master_item_id))
        .forEach(si => {
          const mi = masterItems.find(m => m.id === si.master_item_id);
          if (!mi) return;
          const groupKey = getGroupKey(mi);
          byGroup[groupKey] = (byGroup[groupKey] || 0) + 1;
        });
      return byGroup;
    }, [suggestedItems, masterItems, filteredProjectItems, getGroupKey]);

    // ─── Item mutation handlers (unchanged from original) ───────────

    const prepareItemData = useMemo(
      () =>
        (formData, existingItem = null, relationships = null) => {
          const baseAmount = formData.cost_override || formData.estimated_cost || 0;
          const quantity = formData.quantity || 1;
          const includesVAT = formData.incl_vat ?? true;
          const totalCost = calculateTotalWithVAT({ baseAmount, quantity, includesVAT });

          const itemData = {
            master_item_id: formData.master_item_id,
            space_id: spaceId || null,
            selected_option: formData.selected_option || null,
            estimated_cost: formData.estimated_cost,
            cost_override: formData.cost_override,
            incl_vat: includesVAT,
            total_cost: totalCost,
            quantity,
          };

          if (existingItem) itemData.id = existingItem.id;
          if (relationships) itemData.relationships = relationships;
          return itemData;
        },
      [spaceId],
    );

    const handleAddItem = useCallback(
      async ({ items, cost_type_override, bundleInfo }) => {
        let bundleRelationships = null;
        const bundleName = bundleInfo?.is_bundle
          ? `${bundleInfo.master_item_id}_space${spaceId || 'null'}_${Date.now()}`
          : null;
        if (bundleInfo?.is_bundle) {
          const bundleRel = {
            name: bundleName,
            master_item_id: bundleInfo.master_item_id,
          };
          if (bundleInfo.choice_ancestor) {
            bundleRel.parent_bundle = bundleInfo.choice_ancestor;
          }
          bundleRelationships = { bundle: bundleRel };
        }

        const preparedItems = items.map(item => {
          const isDependency = item.is_dependency;
          let relationships = bundleRelationships;
          if (isDependency && !bundleRelationships) {
            const depName = `dep_${item.master_item_id}_space${spaceId || 'null'}_${Date.now()}`;
            relationships = { dependency: { name: depName, role: 'child' } };
          }
          return prepareItemData(item, null, relationships);
        });

        const isSimpleBundle = bundleInfo?.is_bundle && bundleInfo.bundle_type !== 'choice';
        if (isSimpleBundle) {
          const parentBundleRel = {
            name: bundleName,
            master_item_id: bundleInfo.master_item_id,
            role: 'parent',
          };
          if (bundleInfo.choice_ancestor) {
            parentBundleRel.parent_bundle = bundleInfo.choice_ancestor;
          }
          const parentWrapper = prepareItemData(
            {
              master_item_id: bundleInfo.master_item_id,
              space_id: spaceId || null,
              estimated_cost: 0,
              cost_override: null,
              quantity: 1,
              incl_vat: true,
              selected_option: null,
            },
            null,
            { bundle: parentBundleRel },
          );
          preparedItems.unshift(parentWrapper);
        }

        try {
          await manageProjectItems({
            p_project_id: currentProject.id,
            p_items_data: preparedItems,
            p_cost_type_override: cost_type_override || null,
          });

          // Open the Zone A card for the group we just added to and
          // collapse the Zone B palette so the user sees their new items.
          const firstMasterItemId = preparedItems[0]?.master_item_id;
          if (firstMasterItemId) {
            const targetKey = getGroupKey(firstMasterItemId);
            setExpandedCardKey(targetKey);
            defaultExpandedForSpaceRef.current = spaceId;
          }
          setExpandedPillKey(null);
        } catch (error) {
          logError({
            error,
            severity: ERROR_SEVERITY.ERROR,
            category: ERROR_CATEGORY.DATA_MUTATION,
            message: 'Error creating project items or BOQ items',
          });
          throw error;
        }
      },
      [currentProject?.id, spaceId, manageProjectItems, prepareItemData, logError, getGroupKey],
    );

    // Inline field changes (preserved for potential future use)
    // eslint-disable-next-line no-unused-vars
    const handleFieldChange = useCallback(
      async (itemId, { type, value }) => {
        const projectItem = projectItems?.find(pi => pi.id === itemId);
        if (!projectItem) return;

        const currentState = {
          master_item_id: projectItem.master_item_id,
          estimated_cost: projectItem.estimated_cost,
          cost_override: projectItem.cost_override,
          quantity: projectItem.quantity || 1,
          incl_vat: projectItem.incl_vat ?? true,
          selected_option: projectItem.selected_option,
        };

        let updates = { ...currentState };
        switch (type) {
          case 'quantity':
            updates.quantity = parseFloat(value) || 1;
            break;
          case 'vat':
            updates.incl_vat = value;
            break;
          case 'cost':
            updates.cost_override = value === null ? null : parseFloat(value) || 0;
            break;
          case 'cost_type': {
            await manageProjectItems({
              p_project_id: currentProject.id,
              p_items_data: [{ id: projectItem.id }],
              p_cost_type_override: value,
            });
            return;
          }
          default:
            return;
        }

        await manageProjectItems({
          p_project_id: currentProject.id,
          p_items_data: [prepareItemData(updates, projectItem)],
          p_cost_type_override: null,
        });
      },
      [currentProject?.id, manageProjectItems, prepareItemData, projectItems],
    );

    const handleDelete = useCallback(
      async itemId => {
        const item = projectItems?.find(pi => pi.id === itemId);
        if (!item) return;

        const message =
          `האם למחוק את הפריט "${item.master_items.item_name}"?\n\n` +
          'אזהרה: פעולה זו תמחק את הפריט ורשומות הקשורות אליו, כולל ספקים, רשומות פיננסיות, ופרטי כתב כמויות. ' +
          'לא יהיה ניתן לשחזר פריטים שנמחקו.';

        if (window.confirm(message)) {
          try {
            await deleteProjectItem(itemId);
          } catch (error) {
            logError(error, {
              severity: ERROR_SEVERITY.ERROR,
              category: ERROR_CATEGORY.DATA,
              message: 'Failed to delete project item',
            });
          }
        }
      },
      [projectItems, deleteProjectItem, logError],
    );

    const handleChestAddItems = useCallback(
      items => {
        const simpleItems = [];
        const complexItems = [];
        const preSelectedItems = [];

        items.forEach(item => {
          const defaultOption = projectDefaultOptions?.find(
            opt =>
              opt.master_item_id === item.id &&
              (opt.master_space_id === null || opt.master_space_id === space?.master_space_id),
          );

          if (item.is_bundle) {
            complexItems.push(item);
          } else if (item.item_options?.options && defaultOption?.selected_option) {
            const { cost, notes, ...optionSelections } = defaultOption.selected_option;
            void cost;
            void notes;
            const selectedOption = Object.keys(optionSelections).length > 0 ? optionSelections : {};
            const effectiveCost = getEffectiveEstimatedCost(item, selectedOption);
            preSelectedItems.push({
              master_item_id: item.id,
              quantity: 1,
              estimated_cost: effectiveCost,
              incl_vat: true,
              selected_option: selectedOption,
            });
          } else if (item.item_options?.options) {
            complexItems.push(item);
          } else {
            simpleItems.push(item);
          }
        });

        if (simpleItems.length > 0) {
          handleAddItem({
            items: simpleItems.map(item => ({
              master_item_id: item.id,
              quantity: 1,
              estimated_cost: item.estimated_cost,
              incl_vat: true,
            })),
          });
        }

        if (preSelectedItems.length > 0) {
          handleAddItem({ items: preSelectedItems });
        }

        if (complexItems.length > 0) {
          setWizardItems(complexItems);
          setShowWizard(true);
        }
      },
      [handleAddItem, projectDefaultOptions, space?.master_space_id],
    );

    const handleWizardAddItem = useCallback(
      itemData => {
        handleAddItem(itemData);
      },
      [handleAddItem],
    );

    const handleWizardClose = useCallback(() => {
      setShowWizard(false);
      setWizardItems([]);
    }, []);

    const handleEditItem = useCallback(
      projectItemId => {
        const projectItem = projectItems?.find(pi => pi.id === projectItemId);
        if (!projectItem) return;
        setEditingItem(projectItem);
      },
      [projectItems],
    );

    const handleEditSave = useCallback(
      async updatedConfig => {
        const { id, quantity, cost_override, incl_vat, selected_option, cost_type_id } =
          updatedConfig;

        const projectItem = projectItems?.find(pi => pi.id === id);
        if (!projectItem) return;

        const baseAmount = cost_override || projectItem.estimated_cost || 0;
        const totalCost = calculateTotalWithVAT({
          baseAmount,
          quantity: quantity || 1,
          includesVAT: incl_vat ?? true,
        });

        await manageProjectItems({
          p_project_id: currentProject.id,
          p_items_data: [
            {
              id,
              master_item_id: projectItem.master_item_id,
              space_id: spaceId || null,
              quantity: quantity || 1,
              cost_override,
              incl_vat: incl_vat ?? true,
              total_cost: totalCost,
              selected_option,
            },
          ],
          p_cost_type_override:
            cost_type_id !== projectItem.master_items?.cost_type_id ? cost_type_id : null,
        });
      },
      [currentProject?.id, projectItems, spaceId, manageProjectItems],
    );

    const handleEditClose = useCallback(() => {
      setEditingItem(null);
    }, []);

    // ─── Render ─────────────────────────────────────────────────────

    if (projectItemsError) {
      return <div>שגיאה בטעינת הפריטים</div>;
    }

    if (projectItemsLoading) {
      return <div>Loading...</div>;
    }

    // Compute summary stats for Zone A
    const totalItemCount =
      filteredProjectItems?.filter(
        i => !i.details?.window_id && i.relationships?.bundle?.role !== 'parent',
      ).length || 0;
    const totalCostSum = (filteredProjectItems || []).reduce(
      (sum, i) => sum + (parseFloat(i.total_cost) || 0),
      0,
    );

    return (
      <div>
        {/* ─── Zone A: Your Items ─────────────────────────────────── */}
        <section className={sectionStyles.zoneA}>
          <header className={sectionStyles.zoneAHeader}>
            <div className={sectionStyles.zoneATitleWrap}>
              <h2 className={sectionStyles.zoneATitle}>פריטים בחלל</h2>
              {totalItemCount > 0 && (
                <span className={sectionStyles.zoneACount}>{totalItemCount}</span>
              )}
            </div>
            {totalCostSum > 0 && (
              <span className={sectionStyles.zoneATotal}>{formatCurrencyShort(totalCostSum)}</span>
            )}
          </header>

          {totalItemCount === 0 ? (
            <div className={sectionStyles.emptyState}>
              <div className={sectionStyles.emptyIllus}>
                <Package size={32} weight="duotone" />
              </div>
              <h3 className={sectionStyles.emptyTitle}>עדיין אין פריטים בחלל</h3>
              <p className={sectionStyles.emptySubtitle}>
                בחרו קטגוריה מלוח הקטגוריות למטה כדי להוסיף פריט ראשון
              </p>
              <div className={sectionStyles.emptyArrow} aria-hidden="true">
                <CaretDown size={20} />
              </div>
            </div>
          ) : (
            <div className={sectionStyles.vendorCardsContainer}>
              {cardsWithItems.map(({ key, vendorType, items }) => (
                <TradeCard
                  key={key}
                  vendorType={vendorType}
                  items={items}
                  masterItems={catalogMasterItems}
                  onDelete={handleDelete}
                  onEdit={handleEditItem}
                  bbGroupMap={bbGroupMap}
                  expandedKey={expandedCardKey}
                  onToggleExpand={setExpandedCardKey}
                />
              ))}

              {spaceId && projectWindows?.length > 0 && (
                <TradeCard
                  vendorType={{ id: '__windows__', name: 'חלונות', display_order: 9998 }}
                  items={(filteredProjectItems || []).filter(i => i.details?.window_id)}
                  masterItems={[]}
                  onDelete={handleDelete}
                  onEdit={handleEditItem}
                  windowsContent={<SpaceWindowsSection spaceId={spaceId} spaceData={space} />}
                  windowCount={projectWindows.length}
                  expandedKey={expandedCardKey}
                  onToggleExpand={setExpandedCardKey}
                />
              )}
            </div>
          )}
        </section>

        {/* ─── Zone B: Add Items Palette ──────────────────────────── */}
        {(catalogCards.length > 0 || (spaceId && !isKlali && !isConstruction)) && (
          <section className={sectionStyles.zoneB}>
            <header className={sectionStyles.zoneBHeader}>
              <div className={sectionStyles.zoneBTitleWrap}>
                <Plus size={16} weight="bold" />
                <h2 className={sectionStyles.zoneBTitle}>הוספת פריטים לפי קטגוריה</h2>
              </div>
              <span className={sectionStyles.zoneBSubtitle}>
                {catalogCards.length + (spaceId && !isKlali && !isConstruction ? 1 : 0)} קטגוריות
              </span>
            </header>
            <div className={sectionStyles.categoryPills}>
              {catalogCards.map(({ key, vendorType, currentCount }) => {
                const suggested = missingSuggestedByGroup[key] || 0;
                return (
                  <TradeCard
                    key={key}
                    vendorType={vendorType}
                    items={[]}
                    masterItems={catalogMasterItems}
                    suggestedItems={suggestedItems}
                    projectItems={filteredProjectItems}
                    onAddItems={handleChestAddItems}
                    isLoading={projectItemsLoading}
                    bbGroupMap={bbGroupMap}
                    missingSuggestedCount={suggested}
                    currentCount={currentCount}
                    variant="catalog"
                    expandedKey={expandedPillKey}
                    onToggleExpand={setExpandedPillKey}
                  />
                );
              })}

              {spaceId && !isKlali && !isConstruction && (
                <TradeCard
                  vendorType={{ id: '__windows__', name: 'חלונות', display_order: 9998 }}
                  items={[]}
                  masterItems={[]}
                  suggestedItems={[]}
                  projectItems={[]}
                  onAddItems={handleChestAddItems}
                  isLoading={false}
                  windowsContent={<SpaceWindowsSection spaceId={spaceId} spaceData={space} />}
                  currentCount={projectWindows?.length || 0}
                  onAddWindow={() => openModal(MODAL_TYPES.WINDOW_FORM, { spaceId, space })}
                  variant="catalog"
                  expandedKey={expandedPillKey}
                  onToggleExpand={setExpandedPillKey}
                />
              )}
            </div>
          </section>
        )}

        {/* Modals */}
        {showWizard && wizardItems.length > 0 && (
          <ItemConfigWizard
            items={wizardItems}
            masterItems={masterItems}
            onAddItem={handleWizardAddItem}
            onClose={handleWizardClose}
            costTypes={costTypes}
            spaceId={spaceId}
            projectDefaultOptions={projectDefaultOptions}
            masterSpaceId={space?.master_space_id}
          />
        )}

        {editingItem && (
          <ItemOptionsEditor
            projectItem={editingItem}
            masterItem={masterItems?.find(mi => mi.id === editingItem.master_item_id)}
            onSave={handleEditSave}
            onClose={handleEditClose}
            costTypes={costTypes}
          />
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.spaceId === nextProps.spaceId &&
      prevProps.space?.master_space_id === nextProps.space?.master_space_id &&
      prevProps.isKlali === nextProps.isKlali &&
      prevProps.isConstruction === nextProps.isConstruction
    );
  },
);

export default SpaceItemsSection;
